import {OrganizationUid, UserUid} from "@buildwithflux/identifiers-schema/legacy";
import {getOrganizationRoleAccessListIdentifier, getUserAccessListIdentifier} from "@buildwithflux/permissions-logic";

import {filterTestData} from "./general";

/**
 * Minimal structural view of a profile account (user or organization) sufficient to
 * build profile-page filters. `IUserData` and `Organization` from `@buildwithflux/models`
 * are assignable to it.
 */
export type ProfileAccountForFilters = {
    uid: string;
    handle: string;
};

export function getOwnerFilterForDocumentsInProfilePage(profileUser: ProfileAccountForFilters) {
    // Business rule: once a document is organization owned it disappears from the user-who-created-it's profile
    // Eventually we should use the boolean ISearchData.is_organization_owned to filter out documents that are
    // organization owned, but until we backfill the search indexes, we can use the fact that the owner_handle of
    // an organization document won't match our profile's handle

    const ownerFilter = `owner_uid:${profileUser.uid} && owner_handle:${profileUser.handle}`;

    // TODO: eventually replace with the more rigorous
    // const ownerFilter = `owner_uid:${profileUser.uid} AND is_organization_owned:false`;

    return ownerFilter;
}

// Filters used in User Profile page (React component) - All tab
export function getFiltersForDocumentsInUserProfilePage_AllTab(profileUser: ProfileAccountForFilters) {
    const ownerFilter = getOwnerFilterForDocumentsInProfilePage(profileUser);
    // NOTE: Below we are referring to is_published as it appears on ISearchDocumentData, _not_ on IDocumentData.
    return `${ownerFilter} ${filterTestData(true, "typesense")}`;
}

// Filters used in User Profile page (React component) - Project tab
export function getFiltersForDocumentsInUserProfilePage_ProjectTab(profileUser: ProfileAccountForFilters) {
    const ownerFilter = getOwnerFilterForDocumentsInProfilePage(profileUser);
    // NOTE: Below we are referring to is_published as it appears on ISearchDocumentData, _not_ on IDocumentData.
    return `${ownerFilter} && is_published:false ${filterTestData(true, "typesense")}`;
}
// Filters used in User Profile page (React component) - Component tab
export function getFiltersForDocumentsInUserProfilePage_ComponentTab(profileUser: ProfileAccountForFilters) {
    const ownerFilter = getOwnerFilterForDocumentsInProfilePage(profileUser);

    // NOTE: Below we are referring to is_published as it appears on ISearchDocumentData, _not_ on IDocumentData.
    return `${ownerFilter} && is_published:true ${filterTestData(true, "typesense")}`;
}

// Filters used in Org Profile page (React component) - All tab
export function getFiltersForDocumentsInOrgProfilePage_AllTab(organization: ProfileAccountForFilters) {
    return `owner_handle:${organization.handle} ${filterTestData(true, "typesense")}`;
}

// Filters used in Org Profile page (React component) - Project tab
export function getFiltersForDocumentsInOrgProfilePage_ProjectTab(organization: ProfileAccountForFilters) {
    // NOTE: Below we are referring to is_published as it appears on ISearchDocumentData, _not_ on IDocumentData.
    return `owner_handle:${organization.handle} && is_published:false ${filterTestData(true, "typesense")}`;
}

// Filters used in Org Profile page (React component) - Component tab
export function getFiltersForDocumentsInOrgProfilePage_ComponentTab(organization: ProfileAccountForFilters) {
    // NOTE: Below we are referring to is_published as it appears on ISearchDocumentData, _not_ on IDocumentData.
    return `owner_handle:${organization.handle} && is_published:true ${filterTestData(true, "typesense")}`;
}

// Filters used in User Profile page (React component) - Shared tab
// Returns documents where the user has access via userAccessList but is NOT the owner.
// Uses both the legacy bare uid and the prefixed usr_ identifier for backward compatibility.
export function getFiltersForSharedDocumentsInUserProfilePage(userUid: UserUid) {
    const userAccessIdentifier = getUserAccessListIdentifier(userUid);
    // Match documents where userAccessList contains either the legacy uid or the prefixed identifier,
    // but exclude documents the user owns.
    return `(userAccessList:=${userUid} || userAccessList:=${userAccessIdentifier}) && owner_uid:!=${userUid} ${filterTestData(
        true,
        "typesense",
    )}`;
}

// Filters used in Org Profile page (React component) - Shared tab
// Returns documents shared with an org (via org role entries in userAccessList)
// but NOT owned by the org itself.
export function getFiltersForSharedDocumentsInOrgProfilePage(orgUid: OrganizationUid) {
    const memberIdentifier = getOrganizationRoleAccessListIdentifier(orgUid, "member");
    const ownerIdentifier = getOrganizationRoleAccessListIdentifier(orgUid, "owner");
    // Match documents where userAccessList contains the org member or owner identifier,
    // but exclude documents owned by the org.
    // NOTE: organization_owner_uid does not exist in the live Typesense projects collection,
    // and owner_uid stores the creating user (not the org). We use is_organization_owned
    // (indexed boolean, always present) to exclude org-owned documents — those belong on the
    // org's Overview tab, not the Shared tab.
    return `(userAccessList:=${memberIdentifier} || userAccessList:=${ownerIdentifier}) && is_organization_owned:!=true ${filterTestData(
        true,
        "typesense",
    )}`;
}
